import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { PsvResolvedScopeKind } from "@api/shared-runtime/psv-monitoring";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { SOURCE_LABELS } from "../monitoring-constants";
import {
  describeCoverageGap,
  summarizeResolvedScope,
} from "../monitoring-format";
import type { ResolvedMonitorScope } from "../monitoring-types";

/** What the next monthly check covers, with the detail behind the summary. */
export function MonitoringDetails({ scope }: { scope: ResolvedMonitorScope }) {
  const [open, setOpen] = useState(false);
  const notChecked = [
    ...scope.outOfScope.stateLicenses.map(
      (stateCode) => `${stateCode} license (outside every rule)`,
    ),
    ...scope.coverageGaps.map(describeCoverageGap),
  ];
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-slate-900">What's checked</p>
          <p className="text-sm text-muted-foreground">
            {summarizeResolvedScope(scope)}
          </p>
        </div>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="-mr-2 shrink-0 text-primary max-sm:h-11"
          >
            Details
            <ChevronDown
              aria-hidden="true"
              className={`ml-1 size-4 transition-transform ${open ? "rotate-180" : ""}`}
            />
          </Button>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="space-y-3 rounded-md bg-slate-50 p-3 text-sm">
        <ul className="list-disc space-y-0.5 pl-5">
          {scope.resolved.map(({ family, scope: familyScope }) => (
            <li key={family}>
              {SOURCE_LABELS[family]}
              {familyScope.kind === PsvResolvedScopeKind.AllStates &&
                " — all states"}
              {familyScope.kind === PsvResolvedScopeKind.States &&
                ` — ${familyScope.stateCodes.join(", ")}`}
            </li>
          ))}
        </ul>
        {notChecked.length > 0 && (
          <div>
            <p className="font-medium">Not checked</p>
            <ul className="list-disc space-y-0.5 pl-5 text-muted-foreground">
              {notChecked.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}
